// @flow
import React, {useState} from 'react';
import _ from 'lodash';
import {useIntl, defineMessages} from 'react-intl';
import {Button} from 'sail/Button';
import {useApi, useAccount} from 'docs/data';
import type {Commands} from 'docs/components/contrib/StripeClui/helpers/commands/resource';
import type {GenerateExampleSnippetRequestModelHttpVerb} from 'docs/types';
import {useApiExplorer} from './ApiExplorerProvider';
import {makeStripeRequest} from '../StripeClui/helpers/commands/resource';
import {lookUpResourceByName} from './resources';

type MakeRequestButtonProps = {|
  resources: Commands,
|};

const messages = defineMessages({
  makeRequest: {
    id: 'docsApp.MakeRequestButton.makeRequest',
    description: 'Label on make request button',
    defaultMessage: 'Make a request',
  },
});

export const MakeRequestButton = ({resources}: MakeRequestButtonProps) => {
  const intl = useIntl();
  const {api} = useApi();
  const {account} = useAccount();
  const {
    state: {resource, action, pathArgs, params},
    setCodegenExample,
    setApiResponse,
  } = useApiExplorer();
  const [codegenLoading, setCodegenLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);

  const getPath = (cmd) => {
    // if no arguments, just return the path as is
    if (!(cmd?.args?.length && cmd.args.length > 0)) {
      return cmd?.path;
    }

    // otherwise, replace pathArgs
    const argTemplateHelper = _.transform(pathArgs, function(
      result,
      argInput,
      arg,
    ) {
      result[`{${arg}}`] = argInput;
    });

    const re = new RegExp(Object.keys(argTemplateHelper).join('|'), 'g');

    // TODO: ERROR HANDLING! (check that all path arguments are provided, if not display informative error)
    return cmd?.path?.replace(re, function(matched) {
      return argTemplateHelper[matched];
    });
  };

  const loadCodegenExampleSnippet = async (
    path: string,
    args: Object,
    verb: GenerateExampleSnippetRequestModelHttpVerb,
  ) => {
    const response = await api.generateExampleSnippet({
      path,
      args,
      verb,
    });
    // TODO: Response is currently a string, fix endpoint so we get back a JSON object
    // See this discussion: https://git.corp.stripe.com/stripe-internal/pay-server/pull/422070#discussion_r1673100
    const parsed = JSON.parse(response.toString());

    if (parsed.error) {
      throw new Error(
        'Oops, could not generate example requests.\n\nDid you forget to provide a path argument?',
      );
    }

    setCodegenExample({
      generatedCodeBlocks: Object.entries(parsed).map(
        ([lang, generatedCode]) => ({
          lang,
          generatedCode,
        }),
      ),
    });
  };

  const transformVerb = (
    verb: ?string,
  ): GenerateExampleSnippetRequestModelHttpVerb => {
    if (verb === 'post') {
      return 'post';
    } else if (verb === 'get') {
      return 'get';
    } else if (verb === 'delete') {
      return 'delete';
    }
    throw new Error('Verb must be one of post, get, or delete');
  };

  const handleGenExample = async (path, method) => {
    setCodegenLoading(true);
    try {
      const args = JSON.stringify(params);
      const verb = transformVerb(method);
      if (verb) {
        await loadCodegenExampleSnippet(path, args, verb);
      }
    } catch (err) {
      setCodegenExample({
        error: err.message,
      });
    }
    setCodegenLoading(false);
  };

  const handleMakeRequest = async (path, method) => {
    const isLoggedIn = account?.merchant?.isLoggedIn ?? false;

    if (!isLoggedIn) {
      setApiResponse({error: 'You must be logged in to make a request'});
      return;
    }

    const api_key = account?.keys?.secret;

    setApiLoading(true);
    try {
      const response = await makeStripeRequest({
        url: `${account?.hosts?.['api.stripe.com']}${path}`,
        method,
        api_key,
        raw_params: params ? {...params} : {},
        additional_headers: {},
      });
      setApiResponse({response});
    } catch (err) {
      setApiResponse({error: err.message});
    }
    setApiLoading(false);
  };

  const handleClick = async () => {
    const cmd = lookUpResourceByName(resources, resource)?.[action];
    const path = getPath(cmd);
    const method = cmd?.method;

    if (!(path && method)) {
      setCodegenExample({error: 'This command does not exist'});
      setApiResponse({error: 'This command does not exist'});
      return;
    }

    handleGenExample(path, method);
    handleMakeRequest(path, method);
  };

  return (
    <Button
      label={intl.formatMessage(messages.makeRequest)}
      color="blue"
      size="jumbo"
      disabled={resource === '' || action === ''}
      pending={codegenLoading || apiLoading}
      onClick={handleClick}
    />
  );
};
